import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import englishFlag from '../Assets/United-Kingdom-Flag.png';
import norwegian from '../Assets/NorwegianFlag.png';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { AuthContext } from '../hooks/AuthContext';
import { isUserAuthenticated } from '../utils/user.utils';
import axiosInstance from '../axios.config';
import {
    Container,
    Grid,
    IconButton,
    InputAdornment
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { setUserInfos } from '../redux-store/authSlice/authSlice';
import {
    FullHeightBox,
    TitleTypography,
    SubtitleTypography,
    AbsoluteBox,
    FlagButton,
    FormContainer,
    StyledTextField,
    SubmitButton
} from '../styles/login.styles';
import { processLogin, validateToken } from '../services/user.service';

function Login() {
    const dispatch = useDispatch()
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const [culture, setCulture] = useState('nb-NO');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const { setIsLoggedIn } = useContext(AuthContext);
    const [localizedStrings, setLocalizedStrings] = useState({
        "Login": "Logg Inn",
        "UserName": "Brukernavn",
        "Password": "Passord",
        "Enter your username": "Skriv inn brukernavnet ditt",
        "Enter your password": "Skriv inn passordet ditt",
        "Email":"e-post"
    });

    useEffect(() => {
        (async () => {
            const isValidToken = await validateToken()
            
            // if already authenticated => go to home
            if (isValidToken) {
                navigate('/home');
            } else {
                localStorage.setItem('isLoggedIn', 'null');
            }
        })()
    }, [navigate]);

    useEffect(() => {
        // Fetch localized strings when component mounts
        fetchLocalizedStrings(culture);
    }, [culture]); // Fetch data only once when component mounts

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const fetchLocalizedStrings = async (culture) => {
        try {
            const response = await axiosInstance.get(`/Localization/${culture}`, {
                headers: {
                    'Accept-Language': culture,
                }
            });
            setLocalizedStrings(response.data);
            localStorage.setItem('languageSelected', JSON.stringify(response.data));
            localStorage.setItem('cultureSelected', JSON.stringify(culture));

            return response.data;
        } catch (error) {
            toast.error(localizedStrings['Something went wrong while loading the page'], error);
        }
    };

    const lang = useMemo(() => localizedStrings, [localizedStrings]);

    const handleLogin = e => {
        e.preventDefault();

        processLogin(email, password)
            .then(response => {
                processLoginSuccess(response.data);
            })
            .catch(error => {
                processLoginFail(error)
            });
    };

    const processLoginSuccess = (userData) => {
        localStorage.setItem('userDetails', JSON.stringify(userData));
        localStorage.setItem('isLoggedIn', JSON.stringify(true));
        setIsLoggedIn(true); // context api for auth status 
        dispatch(setUserInfos(userData)) // redux for user data
        navigate('/home');
    }

    const processLoginFail = (error) => {
        if (error.response) {
            toast.error(localizedStrings['Please enter valid UserName and Password']);
        }
        else if (error.request) {
            toast.error('No response received from server. Please try again later.');
        }
        setIsLoggedIn(false);
    }

    return (
        <FullHeightBox>
            <TitleTypography variant="h1" component="div">
                SKEIDAR
            </TitleTypography>
            <SubtitleTypography variant="h2" component="div">
                <b>ELLINOR</b> EDUCATION      
            </SubtitleTypography>

            <ToastContainer
                position="bottom-right"
                autoClose={2000}
                newestOnTop={false}
                closeOnClick
                progressClassName="custom-toast-progress-bar"
            />

            <AbsoluteBox>
                <FlagButton onClick={() => setCulture('en-US')} backgroundImage={englishFlag} title="English" />
                <FlagButton onClick={() => setCulture('nb-NO')} backgroundImage={norwegian} title="Norwegian" />
            </AbsoluteBox>

            <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
                <FormContainer>
                    <form onSubmit={handleLogin}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <StyledTextField
                                    variant="outlined"
                                    fullWidth
                                    label={lang.Email}
                                    placeholder={localizedStrings["Enter your email"] || "Enter your email"}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <StyledTextField
                                    variant="outlined"
                                    fullWidth
                                    type={passwordVisible ? 'text' : 'password'}
                                    label={lang.Password}
                                    placeholder={localizedStrings["Enter your password"] || "Enter your password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="off"
                                    required
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton 
                                                    onClick={togglePasswordVisibility} 
                                                    edge="end"
                                                    sx={{
                                                        color: '#6c757d',
                                                        '&:hover': {
                                                            color: '#dc3545',
                                                        },
                                                    }}
                                                >
                                                    {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 1 }}>
                                <SubmitButton type="submit" variant="contained" fullWidth>
                                    {localizedStrings.Login || "LOGIN"}
                                </SubmitButton>
                            </Grid>
                        </Grid>
                    </form>
                </FormContainer>
            </Container>
        </FullHeightBox>
    );
}

export default Login;
